import React, { useCallback, useEffect, useState } from "react";
import {
  Tabs,
  Input,
  Spin,
  Button,
  message,
  Steps,
  Modal,
  Select,
  Form,
  InputNumber,
  Table,
  Tag,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getOrderReducer } from "../../../redux/Reducer/orderReducer";
import {
  exportOrders,
  getAllAdminOrdersByStatusId,
  resetExport,
  resetOrders,
  searchOrders,
} from "../../../redux/Actions";
import { getAuthReducer } from "../../../redux/Reducer/authReducer";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { ExportIcon, SearchIcon } from "../../../asset/icons/icons";
import { useMatchMedia } from "../../../Utils/media";
import { debounce } from "lodash";
import ExportPopconfirm from "../../../Components/Export/ExportPopconfirm";
import ViewOrderDetailsModal from "../../../Components/OrdersAdmin/ViewOrderDetails";
import moment from "moment";

const newEmpButton = {
  display: "flex",
  height: "44px",
  padding: "10px 16px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  borderRadius: "8px",
  background: "#252A4D",
  color: "#FFF",
  fontFamily: '"MADE TOMMY"',
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "20px",
};

const { Option } = Select;

// Utility function to format numbers as currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
  }).format(value);
};

// ==========================================
// 1. CREATE ORDER WIZARD (Kept exactly as is)
// ==========================================
const CreateOrderStep = ({
  onFinish,
  onCancel,
  setConfettiActive,
  confettiActive,
}) => {
  const { authUser } = useSelector(getAuthReducer);
  const [current, setCurrent] = useState(0);
  const [orderItems, setOrderItems] = useState([]);
  const [initialProducts, setInitialProducts] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState({
    id: "",
    name: "",
  });
  const [selectedLocation, setSelectedLocation] = useState({
    id: authUser?.user?.location_id,
    name: "",
  });
  const [customerList, setCustomerList] = useState([]);
  const [locationList, setLocationList] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isLoadingCreateOrder, setIsLoadingCreateOrder] = useState(false);

  useEffect(() => {
    if (confettiActive === true) {
      setCurrent(0);
    }
  }, [confettiActive, current]);

  const products = () => {
    const bearerToken = authUser.access_token;
    fetch(
      `${window.location.hostname === "localhost" ? process.env.REACT_APP_API_BASE_LOCAL_URL : process.env.REACT_APP_API_BASE_DEV_URL}/products`,
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    )
      .then((response) =>
        response.ok ? response.json() : Promise.reject("Failed"),
      )
      .then((data) => setInitialProducts(data.data))
      .catch((error) => console.error("Error fetching item details:", error));
  };

  const handleCustomerChange = (value) => {
    const customer = customerList.find((cus) => cus.id === value);
    setSelectedCustomer({ id: customer.id, name: customer.name });
  };

  const handleLocationChange = (value) => {
    const location = locationList.find((loc) => loc.id === value);
    setSelectedLocation({ id: location.id, name: location.name });
  };

  const independentDistributors = () => {
    const bearerToken = authUser.access_token;
    fetch(
      `${window.location.hostname === "localhost" ? process.env.REACT_APP_API_BASE_LOCAL_URL : process.env.REACT_APP_API_BASE_DEV_URL}/users?user_type_id=3&status=1`,
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    )
      .then((response) =>
        response.ok ? response.json() : Promise.reject("Failed"),
      )
      .then((data) => setCustomerList(data.data))
      .catch((error) => console.error("Error fetching item details:", error));
  };

  const locations = () => {
    const bearerToken = authUser.access_token;
    fetch(
      `${window.location.hostname === "localhost" ? process.env.REACT_APP_API_BASE_LOCAL_URL : process.env.REACT_APP_API_BASE_DEV_URL}/locations`,
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    )
      .then((response) =>
        response.ok ? response.json() : Promise.reject("Failed"),
      )
      .then((data) => setLocationList(data.data))
      .catch((error) => console.error("Error fetching item details:", error));
  };

  const next = () => setCurrent(current + 1);
  const prev = () => setCurrent(current - 1);

  useEffect(() => {
    products();
    independentDistributors();
    locations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFinish = async () => {
    setIsLoadingCreateOrder(true);
    const payload = {
      user_id: selectedCustomer.id,
      location_id: selectedLocation.id,
      is_internal_order: true,
      is_approved: false,
      items: orderItems.map((item) => ({
        product_id: item.id,
        qty: item.quantity,
      })),
    };

    try {
      const bearerToken = authUser.access_token;
      const headers = {
        Authorization: `Bearer ${bearerToken}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      };
      const response = await fetch(
        `${window.location.hostname === "localhost" ? process.env.REACT_APP_API_BASE_LOCAL_URL : process.env.REACT_APP_API_BASE_DEV_URL}/orders`,
        { method: "POST", headers: headers, body: JSON.stringify(payload) },
      );

      setOrderItems([]);
      setInitialProducts([]);
      setSelectedCustomer({});
      setSelectedLocation({});

      const responseData = await response.json();
      if (responseData?.status === true) {
        message.success("Order created successfully!");
        setConfettiActive(true);
        setCurrent(0);
      } else {
        message.error(
          `Failed to Create Order, Kindly check your order details and try again!`,
        );
      }
      onFinish();
      setIsLoadingCreateOrder(false);
    } catch (error) {
      console.error("Failed to submit order. Please try again", error);
      setIsLoadingCreateOrder(false);
    }
  };

  const handleAddProduct = () => {
    if (selectedProduct && quantity > 0) {
      const newItem = {
        ...selectedProduct,
        quantity,
        subtotal: +Number(selectedProduct?.amount ?? 0).toFixed(2) * quantity,
      };
      setOrderItems([...orderItems, newItem]);
      setSelectedProduct(null);
      setQuantity(1);
    }
  };

  const handleProductSelect = (productId) => {
    const product = initialProducts.find((product) => product.id === productId);
    setSelectedProduct(product);
  };

  const handleQuantityChange = (newQuantity, index) => {
    const updatedItems = orderItems.map((item, idx) => {
      if (idx === index) {
        return {
          ...item,
          quantity: newQuantity,
          subtotal: item.amount * newQuantity,
        };
      }
      return item;
    });
    setOrderItems(updatedItems);
  };

  const handleDeleteProduct = (index) => {
    const newItems = orderItems.filter((_, idx) => idx !== index);
    setOrderItems(newItems);
  };

  const totalAmount = orderItems.reduce((acc, item) => acc + item.subtotal, 0);

  const steps = [
    {
      title: "Customer",
      content: (
        <>
          <div>
            <div className="row">
              <div className="row">
                <div className="col mt-4">
                  <Form.Item label="Select Customer">
                    <Select
                      value={selectedCustomer.id}
                      onChange={handleCustomerChange}
                    >
                      {customerList.map((cus) => (
                        <Option key={cus.id} value={cus.id}>
                          {cus.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item label="Select Location">
                    <Select
                      value={selectedLocation.id}
                      onChange={handleLocationChange}
                      defaultValue={selectedLocation.id}
                      disabled={authUser.user.roles.name !== "Super Admin"}
                    >
                      {locationList.map((loc) => (
                        <Option key={loc.id} value={loc.id}>
                          {loc.name} - {loc.address}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </div>
            </div>
          </div>
        </>
      ),
    },
    {
      title: "Product",
      content: (
        <>
          <div className="mt-4">
            <h6>Choose product and quantity</h6>
            <div className="border rounded-2 py-2">
              <div className="row bg-light mx-1">
                <div className="col-5 fw-bold">PRODUCT</div>
                <div className="col-2 fw-bold">QUANTITY</div>
                <div className="col-2 fw-bold">PRICE</div>
                <div className="col-2 fw-bold">SUBTOTAL</div>
                <div className="col-1 fw-bold"></div>
              </div>

              {orderItems.map((item, index) => (
                <div className="row mx-1 mt-3" key={index}>
                  <div className="col-5">
                    <div className="row">
                      <div className="col-2">
                        <img
                          src={item.images[0]?.url}
                          width={40}
                          height={35}
                          className="border border-1 rounded-2"
                          alt="product"
                        />
                      </div>
                      <div className="col-10">{item.name}</div>
                    </div>
                  </div>
                  <div className="col-2">
                    <InputNumber
                      min={1}
                      value={item.quantity}
                      onChange={(value) => handleQuantityChange(value, index)}
                    />
                  </div>
                  <div className="col-2">{formatCurrency(item.amount)}</div>
                  <div className="col-2">{formatCurrency(item.subtotal)}</div>
                  <div className="col-1">
                    <span
                      className="text-danger mx-1"
                      onClick={() => handleDeleteProduct(index)}
                    >
                      <DeleteOutlined style={{ cursor: "pointer" }} />
                    </span>
                  </div>
                </div>
              ))}
              <hr className="mb-3" />

              <div className="row mx-1">
                <div className="col-6">
                  <Form.Item>
                    <Select
                      value={selectedProduct?.id || ""}
                      onChange={handleProductSelect}
                    >
                      {initialProducts.map((product) => (
                        <Option key={product.id} value={product.id}>
                          {product.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
                <div className="col-2">
                  <Form.Item>
                    <InputNumber
                      min={1}
                      value={quantity}
                      onChange={setQuantity}
                    />
                  </Form.Item>
                </div>
                <div className="col-2">
                  {formatCurrency(
                    +Number(selectedProduct?.amount ?? 0).toFixed(2) || 0.0,
                  )}
                </div>
                <div className="col-2">
                  {formatCurrency(
                    (
                      +Number(selectedProduct?.amount ?? 0).toFixed(2) *
                      quantity
                    ).toFixed(2) || 0.0,
                  )}
                </div>
              </div>

              <div className="d-flex justify-content-end">
                <button
                  className="mx-2 mb-3 addOrder"
                  style={{ backgroundColor: "#131523", color: "white" }}
                  onClick={handleAddProduct}
                >
                  Click to add product <PlusOutlined />
                </button>
              </div>
            </div>

            <div className="d-flex justify-content-end my-5">
              <span>
                TOTAL AMOUNT:{" "}
                <span className="fw-bold">
                  {formatCurrency(totalAmount.toFixed(2))}
                </span>
              </span>
            </div>
          </div>
        </>
      ),
    },
    {
      title: "Review Order",
      content: (
        <>
          <div>
            <div className="row">
              <div className="col mt-4">
                <Form.Item label="Customer">
                  <Input value={selectedCustomer.name} disabled />
                </Form.Item>
                <Form.Item label="Location">
                  <Input value={selectedLocation.name} disabled />
                </Form.Item>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <h6>Product details</h6>
            <div className="border rounded-2 py-2">
              <div className="row bg-light mx-1">
                <div className="col-6 fw-bold">PRODUCT</div>
                <div className="col-2 fw-bold">QUANTITY</div>
                <div className="col-2 fw-bold">PRICE</div>
                <div className="col-2 fw-bold">SUBTOTAL</div>
              </div>

              {orderItems.map((item, index) => (
                <div className="row mx-1 mt-3" key={index}>
                  <div className="col-6">
                    <div className="row">
                      <div className="col-2">
                        <img
                          src={item.images[0]?.url}
                          width={40}
                          height={35}
                          className="border border-1 rounded-2"
                          alt="product"
                        />
                      </div>
                      <div className="col-10">{item.name}</div>
                    </div>
                  </div>
                  <div className="col-2">x{item.quantity}</div>
                  <div className="col-2">{formatCurrency(item.amount)}</div>
                  <div className="col-2">{formatCurrency(item.subtotal)}</div>
                </div>
              ))}

              <div
                className="mt-4 d-flex justify-content-end"
                style={{
                  backgroundColor: "#fffff4",
                  border: "1px solid black",
                }}
              >
                <div className="m-2 mx-3">
                  <div className="mb-1">
                    Subtotal:{" "}
                    <span style={{ marginLeft: "57px" }}>
                      {formatCurrency(totalAmount)}
                    </span>
                  </div>
                  <div className="mb-1">
                    Discount: <span style={{ marginLeft: "55px" }}>0%</span>
                  </div>
                  <div>
                    Total Amount{" "}
                    <span className="fw-bold" style={{ marginLeft: "30px" }}>
                      {formatCurrency(totalAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      <Steps
        current={current}
        items={steps.map((step) => ({ key: step.title, title: step.title }))}
      />
      <div>{steps[current].content}</div>
      <div className="d-flex justify-content-end" style={{ marginTop: 24 }}>
        {current > 0 && (
          <Button onClick={prev} className="mx-3">
            Back
          </Button>
        )}
        {current < steps.length - 1 && (
          <Button type="primary" onClick={next}>
            Continue
          </Button>
        )}
        {current === steps.length - 1 && (
          <Button
            type="primary"
            onClick={handleFinish}
            disabled={isLoadingCreateOrder}
          >
            Done
          </Button>
        )}
        <Button onClick={onCancel} className="mx-3">
          Cancel
        </Button>
      </div>
    </>
  );
};

// ==========================================
// 2. UNIFIED ORDER TABLE (Replaces old files)
// ==========================================
const UnifiedOrderTable = ({
  allAdminOrdersByStatusId,
  isloadingAllAdminOrdersByStatusId,
  filteredData,
  onChange,
  isSearched,
  currentPage,
  pageSize,
  onShowSizeChange,
  handleAllAdminOrderByStatusIdApi,
  modalConfig,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState();

  // Helper to calculate Promo Total
  const calculateCorrectOrderTotal = (record) => {
    if (!record.items || record.items.length === 0)
      return Number(record.amount) || 0;

    let calculatedTotal = 0;
    record.items.forEach((item) => {
      const product = item.product;
      const qty = Number(item.qty) || 0;

      if (
        product &&
        (product.is_promo_product === true || product.is_promo_product === 1)
      ) {
        const promoAmount = Number(product.promo_amount) || 0;
        calculatedTotal += qty * promoAmount; // qty represents bundles here
      } else {
        calculatedTotal +=
          (Number(item.amount) || Number(product?.amount) || 0) * qty;
      }
    });
    return calculatedTotal;
  };

  const hasPromoInOrder = (record) => {
    return record.items?.some(
      (item) =>
        item.product?.is_promo_product === true ||
        item.product?.is_promo_product === 1,
    );
  };

  const handleOrderAction = (value) => {
    setOpen(true);
    setSelectedOrderId(value);
  };

  const [formValues, setFormValues] = useState();
  const onCreate = (values) => {
    setFormValues(values);
    setOpen(false);
  };

  // Prevent row selection from crashing if not used
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const onSelectChange = (newSelectedRowKeys) =>
    setSelectedRowKeys(newSelectedRowKeys);
  const rowSelection = { selectedRowKeys, onChange: onSelectChange };

  const columns = [
    {
      title: "Order ID",
      dataIndex: "id",
      render: (text) => <span className="orderHistory-text">{text ?? ""}</span>,
      sorter: (a, b) => a?.id - b?.id,
    },
    {
      title: "Transanction Reference",
      dataIndex: "reference",
      render: (text, record) => (
        <span className="orderHistory-text">
          {record?.is_internal_order ? (
            <span className="px-2 order-customer-type-text internal-order-text"></span>
          ) : (
            ""
          )}
          {text ?? ""}
          {hasPromoInOrder(record) && (
            <Tag color="gold" style={{ marginLeft: "8px", fontSize: "10px" }}>
              Promo
            </Tag>
          )}
        </span>
      ),
      sorter: (a, b) => a?.reference?.localeCompare(b?.reference),
    },
    {
      title: "Customer",
      dataIndex: "user",
      render: (text, record) => (
        <span className="orderHistory-text">
          {text?.user_type?.id === 5 ? (
            <span className="px-2 order-customer-type-legend-text direct-customer-text"></span>
          ) : text?.user_type?.id === 6 ? (
            <span className="px-2 order-customer-type-legend-text institutionalbuyer-customer-text"></span>
          ) : record?.is_internal_order === 1 ? (
            <span className="px-2 order-customer-type-legend-text internal-order-text"></span>
          ) : (
            <span className="px-2 order-customer-type-text independent-customer-text"></span>
          )}
          {text?.name ?? ""} <br />
        </span>
      ),
      sorter: (a, b) => a?.user?.name?.localeCompare(b?.user?.name),
    },
    {
      title: "Date of Order",
      dataIndex: "created_at",
      render: (text) => (
        <span className="orderHistory-text">
          {moment(text)?.format("MMM Do YYYY")}
        </span>
      ),
      sorter: (a, b) => a?.created_at?.localeCompare(b?.created_at),
    },
    {
      title: "Time of Order",
      dataIndex: "created_at",
      render: (text) => (
        <span className="orderHistory-text">
          {moment(text)?.format("h:mm A")}
        </span>
      ),
      sorter: (a, b) => a?.created_at?.localeCompare(b?.created_at),
    },
    {
      title: "Branch",
      dataIndex: "location",
      render: (text) => (
        <span className="orderHistory-text">{text?.name ?? ""}</span>
      ),
      sorter: (a, b) => a?.location?.name?.localeCompare(b?.location?.name),
    },
    {
      title: "Order Status",
      dataIndex: "order_status",
      render: (text, record) => {
        let statusClass = "";
        switch (text?.id) {
          case 1:
            statusClass = "orderHistory received";
            break;
          case 2:
            statusClass = "orderHistory confirmed";
            break;
          case 3:
            statusClass = "orderHistory processing";
            break;
          case 4:
            statusClass = "orderHistory rejected";
            break;
          default:
            statusClass = "orderHistory approved";
        }
        return (
          <span
            key={text?.id}
            className={statusClass}
            style={{ width: "100%" }}
          >
            {text?.name === "Pick-up"
              ? "Available for pickup"
              : text?.name === "Initial"
                ? "Incoming"
                : text?.name}
          </span>
        );
      },
      sorter: (a, b) =>
        a?.order_status?.name?.localeCompare(b?.order_status?.name),
    },
    {
      title: "Total Amount",
      dataIndex: "amount",
      render: (text, record) => {
        const finalAmount = calculateCorrectOrderTotal(record);
        return (
          <span
            className="orderHistory-text"
            style={{
              fontWeight: hasPromoInOrder(record) ? "bold" : "normal",
              color: hasPromoInOrder(record) ? "#D4AF37" : "inherit",
            }}
          >
            {finalAmount === undefined || finalAmount === null
              ? "N/A"
              : formatCurrency(finalAmount)}
          </span>
        );
      },
      sorter: (a, b) =>
        calculateCorrectOrderTotal(a) - calculateCorrectOrderTotal(b),
    },
    {
      title: "Actions",
      dataIndex: "id",
      render: (text, record) => (
        <Button
          type="text"
          onClick={() => handleOrderAction(text)}
          className="orderHistory-view-details"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="17"
            height="14"
            viewBox="0 0 17 14"
            fill="none"
          >
            <path
              d="M8.3198 0.504883C10.0075 0.504883 11.6075 1.0904 13.0007 2.1744C14.3938 3.25048 15.5799 4.82504 16.4317 6.79523C16.4954 6.94556 16.4954 7.11964 16.4317 7.26206C14.7281 11.2024 11.6951 13.5603 8.3198 13.5603H8.31184C4.9445 13.5603 1.91149 11.2024 0.20792 7.26206C0.144235 7.11964 0.144235 6.94556 0.20792 6.79523C1.91149 2.85486 4.9445 0.504883 8.31184 0.504883H8.3198ZM8.3198 3.86765C6.5605 3.86765 5.13555 5.28396 5.13555 7.0326C5.13555 8.77332 6.5605 10.1896 8.3198 10.1896C10.0711 10.1896 11.4961 8.77332 11.4961 7.0326C11.4961 5.28396 10.0711 3.86765 8.3198 3.86765ZM8.32076 5.05276C9.41136 5.05276 10.303 5.93895 10.303 7.03086C10.303 8.11486 9.41136 9.00104 8.32076 9.00104C7.22219 9.00104 6.3306 8.11486 6.3306 7.03086C6.3306 6.89635 6.34652 6.76975 6.3704 6.64315H6.41021C7.29384 6.64315 8.01029 5.94686 8.04214 5.0765C8.1297 5.06068 8.22523 5.05276 8.32076 5.05276Z"
              fill="#45464E"
            />
          </svg>
        </Button>
      ),
    },
  ];

  return (
    <div className="row mt-4 mb-4">
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={filteredData}
        scroll={{ x: 1000 }}
        onChange={onChange}
        pagination={
          isSearched
            ? {
                current: currentPage,
                pageSize: pageSize,
                total: filteredData?.length,
                showSizeChanger: true,
                showQuickJumper: true,
                onShowSizeChange: onShowSizeChange,
                showTotal: (total) => `Total ${total} items`,
              }
            : {
                current: allAdminOrdersByStatusId?.meta?.current_page,
                pageSize: allAdminOrdersByStatusId?.meta?.per_page,
                showSizeChanger: false,
                pageSizeOptions: ["10", "20", "30", "40"],
                showQuickJumper: true,
                total: allAdminOrdersByStatusId?.meta?.total,
                style: {
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "20px",
                },
              }
        }
      />
      <ViewOrderDetailsModal
        {...modalConfig}
        id={selectedOrderId}
        open={open}
        setOpen={setOpen}
        onCreate={onCreate}
        onCancel={() => setOpen(false)}
        initialValues={{ modifier: "public" }}
        handleAllAdminOrderByStatusIdApi={handleAllAdminOrderByStatusIdApi}
      />
      {/* Hidden div to prevent React errors if formValues is somehow utilized */}
      <div style={{ display: "none" }}>{JSON.stringify(formValues)}</div>
    </div>
  );
};

// ==========================================
// 3. MAIN ORDER MANAGEMENT COMPONENT
// ==========================================
const OrderManagement = (props) => {
  const { setConfettiActive, confettiActive } = props;
  const [tabKey, setTabKey] = useState("1");
  const {
    allAdminOrdersByStatusId,
    isloadingAllAdminOrdersByStatusId,
    isSearchedOrders,
    isExportingOrderLog,
    exportedOrderLogSheet,
  } = useSelector(getOrderReducer);
  const dispatch = useDispatch();
  const [modalVisible, setModalVisible] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { authUser } = useSelector(getAuthReducer);
  const permissions = authUser?.user?.roles?.permissions ?? [];

  const [searchQuery, setSearchQuery] = useState("");
  const [openExport, setOpenExport] = useState(false);
  const [openExportWhenClicked, setOpenExportWhenClicked] = useState(false);
  const [selectedDates, setSelectedDates] = useState(null);

  const hasPermission = (permissionId) => {
    return permissions.some(
      (perm) => perm.permission_id === permissionId && perm.status === 1,
    );
  };
  const handleOpenExport = (newOpen) => setOpenExport(newOpen);
  const handleCloseExport = () => {
    setOpenExport(false);
    setSelectedDates(null);
  };
  const handleDateChange = (dates) => setSelectedDates(dates);

  const handleExport = () => {
    if (Array.isArray(selectedDates) && selectedDates.length !== 0) {
      setOpenExportWhenClicked(true);
      let dates = {
        start_date: selectedDates[0]?.format("YYYY-MM-DD"),
        end_date: selectedDates[1]?.format("YYYY-MM-DD"),
        order_status_id: tabKey,
      };
      dispatch(exportOrders(dates));
    }
  };

  useEffect(() => {
    if (exportedOrderLogSheet) setOpenExportWhenClicked(false);
    return () => resetExport();
  }, [exportedOrderLogSheet]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value) => {
      if (value.length > 0) {
        let payload = { searchQuery: value, order_status_id: tabKey };
        dispatch(searchOrders(payload));
      } else {
        dispatch(resetOrders());
        dispatch(getAllAdminOrdersByStatusId({ order_status_id: tabKey }));
      }
    }, 300),
    [dispatch, tabKey],
  );

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleCancel = () => {
    setModalVisible(false);
    dispatch(getAllAdminOrdersByStatusId({ order_status_id: tabKey }));
  };

  const handleFinish = () => {
    setModalVisible(false);
    dispatch(getAllAdminOrdersByStatusId({ order_status_id: tabKey }));
  };

  useEffect(() => {
    if (tabKey !== undefined && !isSearchedOrders) {
      dispatch(getAllAdminOrdersByStatusId({ order_status_id: tabKey }));
    }
  }, [tabKey, dispatch, isSearchedOrders]);

  useEffect(() => {
    if (allAdminOrdersByStatusId?.data?.length > 0) {
      setFilteredData(allAdminOrdersByStatusId?.data);
      setCurrentPage(1);
      setPageSize(10);
    } else {
      setFilteredData([]);
      setCurrentPage(1);
      setPageSize(10);
    }
  }, [allAdminOrdersByStatusId?.data, isSearchedOrders]);

  const handleAllAdminOrderByStatusIdApi = () => {
    if (tabKey !== undefined) {
      dispatch(getAllAdminOrdersByStatusId({ order_status_id: tabKey }));
    }
  };

  const onTabKeyChange = (key) => setTabKey(key);

  const onChange = (pagination, filters, sorter, extra) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);

    if (!isSearchedOrders) {
      dispatch(
        getAllAdminOrdersByStatusId({
          order_status_id: tabKey,
          page: pagination.current,
        }),
      );
    }
  };

  const onShowSizeChange = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const isMobileDevice = useMatchMedia("(max-width:450px)", true);

  // Common Props for the Unified Tables
  const commonProps = {
    allAdminOrdersByStatusId,
    isloadingAllAdminOrdersByStatusId,
    filteredData,
    onChange,
    isSearched: isSearchedOrders,
    currentPage,
    pageSize,
    onShowSizeChange,
    handleAllAdminOrderByStatusIdApi,
  };

  const items = [
    {
      key: "1",
      label: (
        <div style={{ color: "#252A4D" }}>
          Incoming
          {tabKey === "1" ? (
            <span className="mx-2 tag fw-bold">
              {isSearchedOrders
                ? filteredData?.length
                : filteredData?.length === 0
                  ? 0
                  : allAdminOrdersByStatusId?.meta?.total}
            </span>
          ) : (
            ""
          )}
        </div>
      ),
      children: (
        <Spin spinning={isloadingAllAdminOrdersByStatusId} tip="Loading...">
          <UnifiedOrderTable
            {...commonProps}
            modalConfig={{
              orderStatus: 1,
              initialStage: true,
              processingStage: false,
              loadingStage: false,
              pickUpStage: false,
              orderRejected: false,
              isArchived: false,
              orderRejectedError: "",
            }}
          />
        </Spin>
      ),
    },
    {
      key: "2",
      label: (
        <div style={{ color: "#252A4D" }}>
          Confirmed
          {tabKey === "2" ? (
            <span className="mx-2 tag fw-bold">
              {isSearchedOrders
                ? filteredData?.length
                : filteredData?.length === 0
                  ? 0
                  : allAdminOrdersByStatusId?.meta?.total}
            </span>
          ) : (
            ""
          )}
        </div>
      ),
      children: (
        <Spin spinning={isloadingAllAdminOrdersByStatusId} tip="Loading...">
          <UnifiedOrderTable
            {...commonProps}
            modalConfig={{
              orderStatus: 4,
              initialStage: false,
              processingStage: true,
              loadingStage: false,
              pickUpStage: true,
              orderRejected: false,
              isArchived: false,
              orderRejectedError: "",
            }}
          />
        </Spin>
      ),
    },
    {
      key: "3",
      label: (
        <div style={{ color: "#252A4D" }}>
          Processing
          {tabKey === "3" ? (
            <span className="mx-2 tag fw-bold">
              {isSearchedOrders
                ? filteredData?.length
                : filteredData?.length === 0
                  ? 0
                  : allAdminOrdersByStatusId?.meta?.total}
            </span>
          ) : (
            ""
          )}
        </div>
      ),
      children: (
        <Spin spinning={isloadingAllAdminOrdersByStatusId} tip="Loading...">
          <UnifiedOrderTable
            {...commonProps}
            modalConfig={{
              orderStatus: 3,
              initialStage: false,
              processingStage: true,
              loadingStage: false,
              pickUpStage: false,
              orderRejected: false,
              isArchived: false,
              orderRejectedError: "",
            }}
          />
        </Spin>
      ),
    },
  ];

  return (
    <>
      <div className="p-3 mt-5" style={{ borderRadius: "0px", margin: "0px" }}>
        <div className="row">
          <div className="d-flex justify-content-start">
            <div className="col">
              <span
                style={{
                  width: "189px",
                  flexShrink: 0,
                  color: "#45464E",
                  fontFamily: '"MADE TOMMY"',
                  fontSize: "20px",
                  fontWeight: 500,
                  lineHeight: "28px",
                }}
              >
                Orders &gt; Processing Orders
              </span>
            </div>
            <div className="col d-flex justify-content-end">
              {(hasPermission(6) || hasPermission(15) || hasPermission(12)) && (
                <button
                  className="mx-2"
                  style={newEmpButton}
                  onClick={() => setModalVisible(true)}
                >
                  {isMobileDevice ? "New Order" : "Create new order"}
                </button>
              )}

              <ExportPopconfirm
                placement="bottomLeft"
                cancel={handleCloseExport}
                open={openExport}
                handleOpenChange={handleOpenExport}
                onChange={handleDateChange}
                confirm={handleExport}
                isLoading={isExportingOrderLog}
                selectedDates={selectedDates}
                openWhenClicked={openExportWhenClicked}
              >
                <Button
                  icon={<ExportIcon />}
                  style={{
                    display: "flex",
                    height: "44px",
                    padding: "10px 16px",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                    borderRadius: "8px",
                    border: "1px solid var(--Gray-100, #E6E7EC)",
                    background: "#FFF",
                    fontFamily: '"MADE TOMMY"',
                    fontSize: "14px",
                    color: "#0E2354",
                    marginRight: "5px",
                  }}
                >
                  Export
                </Button>
              </ExportPopconfirm>

              <Modal
                title="Create New Order"
                width={700}
                open={modalVisible}
                onCancel={handleCancel}
                footer={null}
              >
                <Form layout="vertical">
                  <CreateOrderStep
                    onFinish={handleFinish}
                    onCancel={handleCancel}
                    setConfettiActive={setConfettiActive}
                    confettiActive={confettiActive}
                  />
                </Form>
              </Modal>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between mb-3">
          <div className="col-12 mt-3 col-md-5 col-sm-5">
            <Input
              placeholder={"Search by order id, customer name or location"}
              style={{
                width: "100%",
                border: "none",
                color: "#BECOCA",
                backgroundColor: "#F7F7F7",
                cursor: "text",
              }}
              value={searchQuery}
              onChange={handleSearch}
              prefix={<SearchIcon />}
            />
          </div>

          <div className="col-12 mt-3 col-md-6 col-sm-5">
            <span>
              <span className="px-2 order-customer-type-legend-text internal-order-text orderHistory-text"></span>
              <span className="fw-bold" style={{ color: "#328835" }}>
                {" "}
                Internal Order{" "}
              </span>
              <span className="px-2 order-customer-type-legend-text direct-customer-text orderHistory-text"></span>{" "}
              <span className="fw-bold" style={{ color: "#7f2da5" }}>
                {" "}
                Direct Customer{" "}
              </span>
              <span className="px-2 order-customer-type-legend-text independent-customer-text orderHistory-text"></span>{" "}
              <span className="fw-bold" style={{ color: "#b9892e" }}>
                {" "}
                Distributor{" "}
              </span>
              <span className="px-2 order-customer-type-legend-text institutionalbuyer-customer-text orderHistory-text"></span>{" "}
              <span className="fw-bold" style={{ color: "#0000FF" }}>
                {" "}
                Institutional Buyer{" "}
              </span>
            </span>
          </div>
        </div>

        <Tabs
          defaultActiveKey="1"
          className="border mt-2 p-2 overflow-x-hidden"
          items={items}
          onChange={onTabKeyChange}
        />
      </div>
    </>
  );
};
export default OrderManagement;
